/**
 * Typography System
 * Three-font stack for distinct use cases
 */

export const fonts = {
  // Display & Headers - Modern, geometric, clean
  display: '"DM Sans", "Urbanist", system-ui, sans-serif',

  // Body Text - Readable, professional
  body: '"Inter", "Ubuntu", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',

  // Data & Code - Monospace for metrics, IDs, timestamps
  mono: '"JetBrains Mono", "Fira Code", "Consolas", "Monaco", monospace',
} as const;

// Type Scale
export const typography = {
  fontFamily: fonts.body,

  h1: {
    fontFamily: fonts.display,
    fontSize: '3rem', // 48px
    lineHeight: '3.5rem', // 56px
    fontWeight: 700,
    letterSpacing: '-0.02em',
  },

  h2: {
    fontFamily: fonts.display,
    fontSize: '2rem', // 32px
    lineHeight: '2.5rem', // 40px
    fontWeight: 600,
    letterSpacing: '-0.01em',
  },

  h3: {
    fontFamily: fonts.display,
    fontSize: '1.5rem', // 24px
    lineHeight: '2rem', // 32px
    fontWeight: 600,
    letterSpacing: '-0.01em',
  },

  h4: {
    fontFamily: fonts.display,
    fontSize: '1.25rem', // 20px
    lineHeight: '1.75rem', // 28px
    fontWeight: 600,
  },

  h5: {
    fontFamily: fonts.display,
    fontSize: '1.125rem', // 18px
    lineHeight: '1.5rem', // 24px
    fontWeight: 600,
  },

  h6: {
    fontFamily: fonts.display,
    fontSize: '1rem', // 16px
    lineHeight: '1.5rem', // 24px
    fontWeight: 600,
  },

  body1: {
    fontFamily: fonts.body,
    fontSize: '1rem', // 16px
    lineHeight: '1.5rem', // 24px
    fontWeight: 400,
  },

  body2: {
    fontFamily: fonts.body,
    fontSize: '0.875rem', // 14px
    lineHeight: '1.25rem', // 20px
    fontWeight: 400,
  },

  button: {
    fontFamily: fonts.body,
    fontSize: '0.875rem', // 14px
    lineHeight: '1rem', // 16px
    fontWeight: 600,
    textTransform: 'none' as const,
    letterSpacing: '0.01em',
  },

  caption: {
    fontFamily: fonts.body,
    fontSize: '0.75rem', // 12px
    lineHeight: '1rem', // 16px
    fontWeight: 400,
    letterSpacing: '0.02em',
  },

  overline: {
    fontFamily: fonts.body,
    fontSize: '0.75rem', // 12px
    lineHeight: '1rem', // 16px
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.08em',
  },
} as const;

// Utility for data display (metrics, IDs, timestamps)
export const dataTypography = {
  metric: {
    fontFamily: fonts.mono,
    fontSize: '2.5rem', // 40px
    lineHeight: '3rem', // 48px
    fontWeight: 700,
    letterSpacing: '-0.02em',
  },

  metricLabel: {
    fontFamily: fonts.body,
    fontSize: '0.875rem', // 14px
    lineHeight: '1.25rem', // 20px
    fontWeight: 500,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
  },

  code: {
    fontFamily: fonts.mono,
    fontSize: '0.875rem', // 14px
    lineHeight: '1.25rem', // 20px
    fontWeight: 400,
  },

  id: {
    fontFamily: fonts.mono,
    fontSize: '1rem', // 16px
    lineHeight: '1.5rem', // 24px
    fontWeight: 500,
  },
} as const;
